import React, { useMemo } from 'react';
import { 
  Plus, 
  User as UserIcon, 
  Mail, 
  Shield, 
  Activity, 
  GitBranch, 
  Building2, 
  Key, 
  Loader2,
  X
} from 'lucide-react';
import { User, Restaurant } from '../../../services/api';

interface UsersViewProps {
  restaurant: Restaurant;
  users: User[];
  onBack: () => void;
  onCreateUser: () => void;
  onChangePassword: (user: User) => void;
  onUpdateStatus: (userId: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED') => void;
}

export const UsersView = React.memo(function UsersView({
  restaurant,
  users,
  onBack,
  onCreateUser,
  onChangePassword,
  onUpdateStatus,
}: UsersViewProps): React.JSX.Element {
  const safeUsers = useMemo(() => Array.isArray(users) ? users.filter(u => u?._id) : [], [users]);
  const restaurantName = useMemo(() => restaurant?.name || 'Unknown', [restaurant?.name]);

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
              Users - {restaurantName}
            </h2>
            <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">
              Manage restaurant users
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <button
          onClick={onCreateUser}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2 rounded-xl hover:from-amber-500 hover:to-amber-600 transition-all font-medium"
        >
          <Plus className="w-5 h-5" />
          New User
        </button>
      </div>

      {safeUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-6">
            <UserIcon className="w-12 h-12 text-amber-500" />
          </div>
          <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">No users found</h3>
          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-6 text-center max-w-md">
            Get started by creating the first user for this restaurant.
          </p>
          <button
            onClick={onCreateUser}
            className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-medium"
          >
            <Plus className="w-5 h-5" />
            Create User
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {safeUsers.map((user) => {
            if (!user?._id) return null;
            return (
            <div key={user._id} className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              {/* User Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-600/30 flex-shrink-0">
                  <UserIcon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-amber-50 dark:text-amber-50 text-zinc-900 mb-1 truncate">{user.name || 'Unknown'}</h3>
                  {user.email ? (
                    <div className="flex items-center gap-2 text-amber-200/80 dark:text-amber-200/80 text-amber-700 text-sm mb-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate font-medium">{user.email}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-500 text-zinc-500 text-sm mb-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate italic">No email available</span>
                    </div>
                  )}
                </div>
              </div>

              {/* User Info */}
              <div className="space-y-3 mb-4">
                {/* Role Badge */}
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                  <span className="px-3 py-1.5 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 text-amber-400 dark:text-amber-400 text-amber-700 rounded-lg text-xs font-semibold uppercase tracking-wide">
                    {(user.role || 'UNKNOWN').replace('_', ' ')}
                  </span>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  <Activity className={`w-4 h-4 ${
                    user.status === 'ACTIVE' ? 'text-green-400 dark:text-green-400 text-green-600' :
                    user.status === 'SUSPENDED' ? 'text-red-400 dark:text-red-400 text-red-600' :
                    'text-zinc-400 dark:text-zinc-400 text-zinc-600'
                  }`} />
                  <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    user.status === 'ACTIVE' ? 'bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700' :
                    user.status === 'SUSPENDED' ? 'bg-red-500/20 dark:bg-red-500/20 bg-red-100 text-red-400 dark:text-red-400 text-red-700' :
                    'bg-zinc-500/20 dark:bg-zinc-500/20 bg-zinc-100 text-zinc-400 dark:text-zinc-400 text-zinc-600'
                  }`}>
                    {user.status || 'UNKNOWN'}
                  </span>
                </div>

                {/* Branch Info */}
                {user.branchId && typeof user.branchId === 'object' && user.branchId.name && (
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <span className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 truncate">
                      Branch: {user.branchId.name}
                    </span>
                  </div>
                )}

                {/* Restaurant Info */}
                {user.restaurantId && typeof user.restaurantId === 'object' && user.restaurantId.name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <span className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 truncate">
                      {user.restaurantId.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-zinc-700/50 dark:border-zinc-700/50 border-zinc-300/50">
                <button
                  onClick={() => onChangePassword(user)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600/20 dark:bg-blue-600/20 bg-blue-100 hover:bg-blue-600/30 dark:hover:bg-blue-600/30 hover:bg-blue-200 text-blue-400 dark:text-blue-400 text-blue-700 rounded-xl text-sm font-medium transition-all"
                  title="Change Password"
                >
                  <Key className="w-4 h-4" />
                  <span className="hidden sm:inline">Password</span>
                </button>
                {user.status === 'ACTIVE' && (
                  <button
                    onClick={() => onUpdateStatus(user._id, 'SUSPENDED')}
                    className="flex-1 px-3 py-2 bg-red-600/20 dark:bg-red-600/20 bg-red-100 hover:bg-red-600/30 dark:hover:bg-red-600/30 hover:bg-red-200 text-red-400 dark:text-red-400 text-red-700 rounded-xl text-sm font-medium transition-all"
                  >
                    Suspend
                  </button>
                )}
                {(user.status === 'SUSPENDED' || user.status === 'INACTIVE') && (
                  <button
                    onClick={() => onUpdateStatus(user._id, 'ACTIVE')}
                    className="flex-1 px-3 py-2 bg-green-600/20 dark:bg-green-600/20 bg-green-100 hover:bg-green-600/30 dark:hover:bg-green-600/30 hover:bg-green-200 text-green-400 dark:text-green-400 text-green-700 rounded-xl text-sm font-medium transition-all"
                  >
                    Activate
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

